import React, { useState } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import restaurantImg from "../assets/restorent/restaurant.png";

/* ─── Schemas ───────────────────────────────────────────────────── */
const krishnagiriFAQSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: "What makes Barbequeen the best restaurant in Krishnagiri?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Since 1999, we've served freshly cooked, generously portioned food built on real charcoal grilling and slow-cooked biryani. Handy for highway travellers and local families alike, it's why diners rate us the best restaurant in Krishnagiri.",
      },
    },
    {
      "@type": "Question",
      name: "Which is the best biryani in Krishnagiri?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Our biryani layers aged basmati with freshly marinated meat over a gentle dum. Fluffy, aromatic rice and tender meat make it a top pick for the best biryani in Krishnagiri.",
      },
    },
    {
      "@type": "Question",
      name: "Where can I get the best chicken biryani in Krishnagiri?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "At Barbequeen. We cook juicy, spice-marinated chicken into fragrant basmati until every layer is rich and flavourful, never dry, making it the best chicken biryani in Krishnagiri.",
      },
    },
    {
      "@type": "Question",
      name: "Where is the best mandi biryani in Krishnagiri?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Our Arabian mandi tops fragrant spiced rice with meat so tender it falls off the bone, cooked low and slow. Smoky and aromatic, it's the best mandi biryani in Krishnagiri, available in chicken or mutton.",
      },
    },
    {
      "@type": "Question",
      name: "Where is Barbequeen located in Krishnagiri, and what are the timings?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "You'll find us at the Chennai exit point on the Chennai to Bengaluru highway, near K.P.N Lakshmi Mahal. We're open daily, 11:00 AM to 11:00 PM, for dine-in, takeaway, and delivery.",
      },
    },
  ],
};

const krishnagiriRestaurantSchema = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://barbequeen.com/#organization",
      name: "Barbequeen Restaurant",
      url: "https://barbequeen.com/",
      logo: "https://barbequeen.com/images/logo.png",
      foundingDate: "1999",
      sameAs: [
        "https://facebook.com/BarbequeenRestaurant/",
        "https://instagram.com/barbequeenrestaurant/",
      ],
    },
    {
      "@type": "Restaurant",
      "@id":
        "https://barbequeen.com/best-restaurant-in-krishnagiri#krishnagiri",
      name: "Barbequeen - Krishnagiri",
      image: "https://barbequeen.com/images/barbequeen-krishnagiri.jpg",
      url: "https://barbequeen.com/best-restaurant-in-krishnagiri",
      telephone: "+919360866880",
      priceRange: "₹₹",
      servesCuisine: [
        "Biryani",
        "Barbecue",
        "Grill",
        "Tandoori",
        "Arabian",
        "North Indian",
        "Chinese",
      ],
      parentOrganization: { "@id": "https://barbequeen.com/#organization" },
      address: {
        "@type": "PostalAddress",
        streetAddress:
          "Chennai Exit Point, Chennai-Bengaluru Highway, Near K.P.N Lakshmi Mahal",
        addressLocality: "Krishnagiri",
        addressRegion: "Tamil Nadu",
        postalCode: "635001",
        addressCountry: "IN",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: "12.5266",
        longitude: "78.2141",
      },
      openingHoursSpecification: {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: [
          "Monday",
          "Tuesday",
          "Wednesday",
          "Thursday",
          "Friday",
          "Saturday",
          "Sunday",
        ],
        opens: "11:00",
        closes: "23:00",
      },
    },
  ],
};

const FAQS = [
  {
    q: "What makes Barbequeen the best restaurant in Krishnagiri?",
    a: "Since 1999, we've served freshly cooked, generously portioned food built on real charcoal grilling and slow-cooked biryani. Handy for highway travellers and local families alike, it's why diners rate us the best restaurant in Krishnagiri.",
  },
  {
    q: "Which is the best biryani in Krishnagiri?",
    a: "Our biryani layers aged basmati with freshly marinated meat over a gentle dum. Fluffy, aromatic rice and tender meat make it a top pick for the best biryani in Krishnagiri.",
  },
  {
    q: "Where can I get the best chicken biryani in Krishnagiri?",
    a: "At Barbequeen. We cook juicy, spice-marinated chicken into fragrant basmati until every layer is rich and flavourful, never dry, making it the best chicken biryani in Krishnagiri.",
  },
  {
    q: "Where is the best mandi biryani in Krishnagiri?",
    a: "Our Arabian mandi tops fragrant spiced rice with meat so tender it falls off the bone, cooked low and slow. Smoky and aromatic, it's the best mandi biryani in Krishnagiri, available in chicken or mutton.",
  },
  {
    q: "Where is Barbequeen located in Krishnagiri, and what are the timings?",
    a: "You'll find us at the Chennai exit point on the Chennai to Bengaluru highway, near K.P.N Lakshmi Mahal. We're open daily, 11:00 AM to 11:00 PM, for dine-in, takeaway, and delivery.",
  },
];

const OTHER_GUIDES = [
  {
    title: "Best Restaurant in Salem",
    path: "/best-restaurant-in-salem",
    city: "Salem",
  },
  {
    title: "Best Restaurant in Coimbatore",
    path: "/best-restaurant-in-coimbatore",
    city: "Coimbatore",
  },
  {
    title: "Best Restaurant in Erode",
    path: "/best-restaurant-in-erode",
    city: "Erode",
  },
  {
    title: "Best Restaurant in Pudukkottai",
    path: "/best-restaurant-in-pudukkottai",
    city: "Pudukkottai",
  },
];

const BestRestaurantInKrishnagiriPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (idx) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const pageTitle = "Best Restaurant in Krishnagiri | Barbequeen Biryani Grill";
  const pageUrl = "https://barbequeen.com/best-restaurant-in-krishnagiri";

  return (
    <div
      className="min-h-screen bg-[#f9f5f0]"
      style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif" }}
    >
      {/* ── SEO & Schemas ── */}
      <SEO
        title={pageTitle}
        metaTitle={pageTitle}
        description="Best biryani, chicken biryani & mandi in Krishnagiri. Charcoal-grilled favourites on the Chennai–Bengaluru highway since 1999. Order online or dine in today."
        keywords="Best Restaurant in Krishnagiri, Best Biryani in Krishnagiri, Best Chicken Biryani in Krishnagiri, Best Mandi Biryani in Krishnagiri"
        robots="index, follow"
        canonical={pageUrl}
        geo={{
          region: "IN-TN",
          placename: "Krishnagiri, Tamil Nadu",
          position: "12.5266;78.2141",
          icbm: "12.5266, 78.2141",
        }}
        og={{
          type: "website",
          url: pageUrl,
          title: pageTitle,
          description:
            "Famous biryani, chicken biryani & Arabian mandi in Krishnagiri. A perfect highway stop for dine-in, takeaway & delivery.",
          image: "https://barbequeen.com/images/barbequeen-krishnagiri-og.jpg",
          imageWidth: "1200",
          imageHeight: "630",
          siteName: "Barbequeen Restaurant",
          locale: "en_IN",
        }}
        schema={[krishnagiriRestaurantSchema, krishnagiriFAQSchema]}
      />

      {/* ── Hero Banner (Blog Style) ── */}
      <section
        className="relative overflow-hidden"
        style={{ minHeight: "360px" }}
      >
        <img
          src="https://images.unsplash.com/photo-1544025162-d76694265947?q=80&w=1600&auto=format&fit=crop"
          alt="Best Restaurant in Krishnagiri - Barbequeen"
          className="absolute inset-0 w-full h-full object-cover object-center scale-105"
          onError={(e) => {
            e.target.src = restaurantImg;
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(to bottom, rgba(10,0,2,0.82) 0%, rgba(10,0,2,0.92) 100%)",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 pt-28 sm:pt-32 pb-14 text-left">
          {/* Breadcrumbs */}
          <nav className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-white/60 mb-5">
            <Link to="/" className="hover:text-white transition-colors">
              Home
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <Link to="/blog" className="hover:text-white transition-colors">
              Blog
            </Link>
            <svg
              className="w-3 h-3 text-white/40"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
            <span className="text-white/90 font-medium truncate max-w-[200px] sm:max-w-xs">
              Krishnagiri Guide
            </span>
          </nav>

          {/* Category Tag */}
          <span className="inline-block px-3 py-1 rounded-full text-white text-[10px] font-bold tracking-[0.2em] uppercase mb-4 bg-[#9b1c2e]">
            Restaurant Guide • Krishnagiri
          </span>

          {/* Title */}
          <h1 className="text-2xl sm:text-4xl lg:text-[40px] font-bold text-white leading-snug tracking-wide uppercase max-w-4xl mb-4">
            Best Restaurant in Krishnagiri - Barbequeen Restaurant
          </h1>

          {/* Meta Bar */}
          <div className="flex flex-wrap items-center gap-5 text-xs text-white/80 font-sans pt-2">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-full bg-[#9b1c2e] flex items-center justify-center text-white font-bold text-xs">
                B
              </div>
              <span className="font-semibold text-white">
                Barbequeen Editorial
              </span>
            </div>
            <span className="text-white/30">•</span>
            <div className="flex items-center gap-1.5 text-white/70">
              <svg
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>5 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Author / Social Share Row ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <div className="flex flex-wrap items-center justify-between gap-4 py-5 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#9b1c2e] flex items-center justify-center flex-shrink-0 text-white font-bold text-sm">
              B
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-800">
                Barbequeen Editorial
              </p>
              <p className="text-xs text-gray-400">
                Serving Krishnagiri Highway & Tamil Nadu Since 1999
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="px-3 py-1 rounded-full border border-gray-200 text-xs font-semibold text-gray-600 uppercase tracking-wide bg-white">
              Krishnagiri City Guide
            </span>
            <span className="text-sm text-gray-500">
              Chennai–Bengaluru Highway
            </span>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-sm text-gray-400 font-medium">Share:</span>
            <a
              href="https://instagram.com/barbequeenrestaurant"
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#9b1c2e] hover:text-[#9b1c2e] transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
              </svg>
            </a>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#9b1c2e] hover:text-[#9b1c2e] transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.746l7.73-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </a>
            <a
              href={`https://facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:border-[#9b1c2e] hover:text-[#9b1c2e] transition-colors"
            >
              <svg
                className="w-3.5 h-3.5"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* ── Main Content + Sidebar Layout ── */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* ── Left Column: Article Body ── */}
          <main className="lg:col-span-8">
            {/* Excerpt */}
            <p className="text-[16px] text-gray-700 leading-[1.85] mb-8 italic border-l-4 border-[#9b1c2e] pl-4 bg-white p-4 rounded-r-lg shadow-sm">
              Whether you are a highway traveller on the Chennai–Bengaluru route
              or a local resident in Krishnagiri looking for an authentic meal,
              Barbequeen is the ultimate food haven. Known for its aromatic
              wood-fire dum biryanis, slow-cooked Arabian mandi, and signature
              charcoal-grilled chicken, Barbequeen is proudly celebrated as the{" "}
              <strong>Best Restaurant in Krishnagiri</strong>.
            </p>

            {/* Section 1 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-8 mb-4 border-b border-gray-200 pb-2">
              The Best Biryani in Krishnagiri
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              At Barbequeen, our biryani is an art form perfected over two
              decades. We use premium aged basmati rice and hand-selected South
              Indian whole spices, slow-cooking tender chicken or mutton in
              sealed handis over gentle dum. The result is fluffy, non-greasy,
              intensely flavorful biryani where every morsel is packed with
              authentic taste.
            </p>

            {/* Section 2 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-8 mb-4 border-b border-gray-200 pb-2">
              The Best Chicken Biryani in Krishnagiri
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              What sets our Chicken Biryani apart is the freshness of the meat
              and our balanced blend of house-ground spices. The chicken is
              marinated overnight in yogurt and herbs, ensuring that it remains
              succulent and juicy throughout the slow dum process. Served with
              spicy brinjal salna, cooling onion raita, and boiled egg, it is
              unequivocally the best chicken biryani in Krishnagiri.
            </p>

            {/* Section 3 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-8 mb-4 border-b border-gray-200 pb-2">
              The Best Mandi Biryani in Krishnagiri
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Looking for a royal Arabian feast? Our Mandi Biryani is cooked low
              and slow until the meat literally falls off the bone. Served over
              an expansive platter of aromatic spiced rice and garnished with
              fried cashews, raisins, and mild Arabian spices, it brings
              friends, families, and road-trippers together for an unforgettable
              sharing feast.
            </p>

            {/* Section 4 */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-8 mb-4 border-b border-gray-200 pb-2">
              Signature Charcoal Grills & Multi-Cuisine Spread
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-3">
              In addition to our world-class biryanis, Barbequeen Krishnagiri is
              famous for its charcoal barbecue, tandoori breads, and
              multi-cuisine favorites:
            </p>
            <div className="space-y-2 mb-6">
              {[
                "Double Marinated Grill Chicken & Chicken Shawarma",
                "Tandoori Chicken, Malai Tikka & Garlic Butter Naan",
                "Dragon Chicken, Chilli Fish & Schezwan Fried Rice",
                "Paneer Butter Masala, Kadai Mushroom & tasty vegetarian meals",
              ].map((item, idx) => (
                <div key={idx} className="flex gap-3 items-start">
                  <span className="w-2 h-2 rounded-full bg-[#9b1c2e] mt-2 flex-shrink-0" />
                  <p className="text-[15px] text-gray-700 leading-[1.8] flex-1">
                    {item}
                  </p>
                </div>
              ))}
            </div>

            {/* Section 5: Outlet Details */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-10 mb-4 border-b border-gray-200 pb-2">
              Visit Barbequeen in Krishnagiri
            </h2>
            <div className="bg-white rounded-xl p-6 border border-gray-200 hover:border-[#9b1c2e]/40 hover:shadow-md transition-all duration-200 mb-8">
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                Barbequeen Restaurant - Highway Outlet
              </h3>
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                Chennai Exit Point, Chennai to Bengaluru Highway, Near K.P.N
                Lakshmi Mahal, Krishnagiri, Tamil Nadu 635001
              </p>
              <div className="flex flex-wrap items-center justify-between gap-4 pt-4 border-t border-gray-100 text-sm">
                <a
                  href="tel:+919360866880"
                  className="font-bold text-[#9b1c2e] hover:underline flex items-center gap-1.5"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                    />
                  </svg>
                  +91 93608 66880
                </a>
                <a
                  href="https://google.com/maps/search/?api=1&query=Barbequeen+Chennai+Bengaluru+Highway+Krishnagiri"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-[#9b1c2e] font-semibold"
                >
                  View on Google Maps →
                </a>
              </div>
            </div>

            {/* Section 6: Highway Pitstop & Dine-In */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-8 mb-4 border-b border-gray-200 pb-2">
              The Ideal Highway Pitstop & Family Diner
            </h2>
            <p className="text-[15px] text-gray-600 leading-[1.85] mb-4">
              Open daily from <strong>11:00 AM to 11:00 PM</strong> with ample
              parking, spacious seating, and hygienic restrooms, Barbequeen
              Krishnagiri is the perfect stop for travellers heading between
              Chennai and Bengaluru, as well as local foodies looking for the
              best dining experience in town.
            </p>

            {/* Section 7: FAQs */}
            <h2 className="text-2xl font-bold text-gray-900 leading-snug mt-12 mb-6 border-b border-gray-200 pb-2">
              Frequently Asked Questions
            </h2>
            <div className="space-y-3 mb-10">
              {FAQS.map((faq, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden transition-all duration-200"
                >
                  <button
                    onClick={() => toggleFaq(idx)}
                    className="w-full px-5 py-4 text-left flex items-center justify-between gap-4 font-semibold text-gray-800 text-sm hover:text-[#9b1c2e] transition-colors"
                  >
                    <span>{faq.q}</span>
                    <span className="text-[#9b1c2e] text-lg font-bold flex-shrink-0">
                      {openFaq === idx ? "−" : "+"}
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4 text-xs sm:text-sm text-gray-600 leading-relaxed border-t border-gray-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Tags / Bottom Share */}
            <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <span className="font-semibold text-gray-700">Tags:</span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Best Restaurant in Krishnagiri
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Krishnagiri Biryani
                </span>
                <span className="bg-gray-100 px-2.5 py-1 rounded-md">
                  Highway Food
                </span>
              </div>
              <a
                href={`https://wa.me/?text=${encodeURIComponent(pageTitle + " " + pageUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#25d366] text-white text-xs font-bold rounded-lg hover:opacity-90 transition-opacity"
              >
                Share on WhatsApp
              </a>
            </div>
          </main>

          {/* ── Right Column: Sidebar ── */}
          <aside className="lg:col-span-4 space-y-6">
            {/* Other Cities Widget */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100 flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-[#9b1c2e]"></span>
                Other City Guides
              </h3>
              <div className="space-y-3">
                {OTHER_GUIDES.map((guide, idx) => (
                  <Link
                    key={idx}
                    to={guide.path}
                    className="group block p-3 rounded-xl bg-gray-50 hover:bg-[#9b1c2e]/5 border border-transparent hover:border-[#9b1c2e]/20 transition-all duration-200"
                  >
                    <span className="text-xs font-bold uppercase tracking-wider text-[#9b1c2e] block mb-0.5">
                      {guide.city}
                    </span>
                    <p className="text-sm font-semibold text-gray-800 group-hover:text-[#9b1c2e] transition-colors">
                      {guide.title} →
                    </p>
                  </Link>
                ))}
              </div>
            </div>

            {/* Quick Actions Widget */}
            <div className="bg-[#1a0a0d] text-white rounded-2xl p-6 shadow-md">
              <span className="text-[10px] font-bold tracking-[0.25em] text-[#c0424e] uppercase block mb-2">
                SINCE 1999
              </span>
              <h3 className="text-lg font-bold mb-3">
                Visit Barbequeen Krishnagiri
              </h3>
              <p className="text-xs text-gray-300 leading-relaxed mb-5">
                Stop by on the Chennai–Bengaluru Highway for the best biryani,
                mandi, and charcoal-grilled chicken in Krishnagiri.
              </p>
              <div className="space-y-2.5">
                <Link
                  to="/menu"
                  className="block w-full py-2.5 text-center bg-[#9b1c2e] hover:bg-[#801423] text-white text-xs font-bold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Explore Full Menu
                </Link>
                <Link
                  to="/contact-us"
                  className="block w-full py-2.5 text-center bg-white/10 hover:bg-white/20 border border-white/20 text-white text-xs font-bold rounded-xl uppercase tracking-wider transition-colors"
                >
                  Contact & Directions
                </Link>
              </div>
            </div>

            {/* Why Barbequeen Widget */}
            <div className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm">
              <h3 className="text-base font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">
                Why Diners Choose Us
              </h3>
              <ul className="space-y-3 text-xs text-gray-600">
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Authentic Dum Biryani:</strong> Slow-cooked aged
                    basmati and fresh meat.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Arabian Mandi:</strong> Fall-off-the-bone tender
                    meat platter.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>Highway Accessibility:</strong> Convenient parking
                    and family seating.
                  </span>
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="text-[#9b1c2e] font-bold">✓</span>
                  <span>
                    <strong>25+ Years Legacy:</strong> Consistent quality and
                    delicious food.
                  </span>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BestRestaurantInKrishnagiriPage;
